// Profile.js
"use client"

import { useState, useEffect, useContext, useRef } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { AuthContext } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import "./Profile.css"

const Profile = () => {
  const { user, updateProfile } = useContext(AuthContext)
  const { t, direction } = useLanguage()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    district: "",
    profilePhoto: null,
  })

  const [countries, setCountries] = useState([])
  const [districts, setDistricts] = useState([])
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [profileCompleted, setProfileCompleted] = useState(false)
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const countriesRes = await axios.get(`${API_URL}/api/locations/countries`)
        setCountries(countriesRes.data)

        const profileRes = await axios.get(`${API_URL}/api/profile`)
        const profileData = profileRes.data.profile // ✅ updated line

        setProfileCompleted(user?.profileCompleted || false)

        setFormData({
          name: user?.name || "",
          email: user?.email || "",
          country: profileData?.country?._id || "",
          district: profileData?.district?._id || "",
          profilePhoto: profileData?.profilePhoto || null,
        })

        if (profileData?.profilePhoto) {
          setPreviewUrl(`${API_URL}/uploads/profile/${profileData.profilePhoto}`)
        }

        if (profileData?.country?._id) {
          const districtsRes = await axios.get(`${API_URL}/api/locations/districts/${profileData.country._id}`)
          setDistricts(districtsRes.data)
        }

        setLoading(false)
      } catch (err) {
        setError("Failed to load profile data")
        setLoading(false)
        console.error(err)
      }
    }
    fetchData()
  }, [user])

  useEffect(() => {
    if (!formData.country) return
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/locations/districts/${formData.country}`)
        setDistricts(res.data)
      } catch (err) {
        setError("Failed to fetch districts")
        console.error(err)
      }
    }
    fetchDistricts()
  }, [formData.country])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "country") {
      setFormData({ ...formData, country: value, district: "" })
    } else {
      setFormData({ ...formData, [name]: value })
    }
    setMessage(null)
    setError(null)
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData({ ...formData, profilePhoto: file })
      const reader = new FileReader()
      reader.onloadend = () => setPreviewUrl(reader.result)
      reader.readAsDataURL(file)
    }
  }

  const triggerFileInput = () => fileInputRef.current.click()

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const profileFormData = new FormData()
      profileFormData.append("name", formData.name)
      profileFormData.append("email", formData.email)
      profileFormData.append("country", formData.country)
      profileFormData.append("district", formData.district)
      if (formData.profilePhoto instanceof File) {
        profileFormData.append("profilePhoto", formData.profilePhoto)
      }
      const result = await updateProfile(profileFormData)
      if (result && result.success) {
        setMessage("Profile updated successfully")
        setTimeout(() => navigate("/dashboard"), 2000)
      } else {
        setError(result.error || "Failed to update profile")
      }
    } catch (err) {
      setError("Something went wrong")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading && !formData.name) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>{t("Loading profile...")}</p>
      </div>
    )
  }

  return (
    <div className="profile-container" dir={direction}>
      <div className="profile-header">
        <h1>{t("User Profile")}</h1>
        <p className="profile-subtitle">{t("Update your personal information")}</p>
        {profileCompleted && !editMode && (
          <button onClick={() => setEditMode(true)} className="edit-button">
            {t("Edit")}
          </button>
        )}
      </div>

      {message && <div className="alert-success profile-alert">{t(message)}</div>}
      {error && <div className="alert-error profile-alert">{t(error)}</div>}

      <div className="profile-card">
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="profile-photo-section">
            <div className="profile-photo" onClick={triggerFileInput} style={{ backgroundImage: previewUrl ? `url(${previewUrl})` : "none" }}>
              {!previewUrl && <i className="fas fa-user"></i>}
              <div className="photo-overlay"><i className="fas fa-camera"></i></div>
            </div>
            <input type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/*" className="hidden-file-input" />
            <p className="photo-hint">{t("Click to upload profile photo")}</p>
          </div>

          <div className="profile-section">
            <h2 className="section-title">{t("Personal Information")}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">{t("Name")}</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="form-control" disabled={profileCompleted && !editMode} />
              </div>
              <div className="form-group">
                <label htmlFor="email">{t("Email")}</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="form-control" disabled={profileCompleted && !editMode} />
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2 className="section-title">{t("Location")}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">{t("Country")}</label>
                <select id="country" name="country" value={formData.country} onChange={handleChange} className="form-control" disabled={profileCompleted && !editMode} required>
                  <option value="">{t("Select a country")}</option>
                  {countries.map((country) => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="district">{t("District")}</label>
                <select id="district" name="district" value={formData.district} onChange={handleChange} className="form-control" disabled={!formData.country || (profileCompleted && !editMode)} required>
                  <option value="">{t("Select a district")}</option>
                  {districts.map((district) => (
                    <option key={district._id} value={district._id}>{district.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {(!profileCompleted || editMode) && (
            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? t("Updating...") : t("Update Profile")}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default Profile
